import {
  plannedLessonSortSchema,
  plannedLessonStatusSchema,
} from '@platform/schema/curriculum';
import { z } from 'zod';

/**
 * What the Curriculum table is showing, kept in the page address: a refresh or a shared
 * link reopens the same view, and the tab itself opens on curriculum order. Anything the
 * address holds that is not a valid value falls back to the default.
 */
export const PAGE_SIZES = [25, 50, 100] as const;

const viewSchema = z.object({
  q: z.string().catch(''),
  courseId: z.string().catch(''),
  moduleId: z.string().catch(''),
  status: plannedLessonStatusSchema.or(z.literal('')).catch(''),
  published: z.enum(['yes', 'no', '']).catch(''),
  sort: plannedLessonSortSchema.catch('position'),
  order: z.enum(['asc', 'desc']).catch('asc'),
  limit: z.coerce
    .number()
    .refine((n) => (PAGE_SIZES as readonly number[]).includes(n))
    .catch(50),
  offset: z.coerce.number().int().min(0).catch(0),
});

export type View = z.infer<typeof viewSchema>;

export const DEFAULT_VIEW: View = viewSchema.parse({});

export const readView = (params: URLSearchParams): View =>
  viewSchema.parse(Object.fromEntries(params));

/** The address for a view, naming only what differs from the default. */
export function writeView(view: View): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of Object.keys(DEFAULT_VIEW) as (keyof View)[]) {
    if (view[key] !== DEFAULT_VIEW[key]) params.set(key, String(view[key]));
  }
  return params;
}

/** The filters alone: what decides which lessons match, and so what "all" selects. */
export const filtersOf = (view: View) => {
  const { q, courseId, moduleId, status, published } = view;
  return {
    ...(q.trim() !== '' && { q: q.trim() }),
    ...(courseId !== '' && { courseId }),
    ...(moduleId !== '' && { moduleId }),
    ...(status !== '' && { status }),
    ...(published !== '' && { published }),
  };
};

export const sameFilters = (a: View, b: View): boolean =>
  JSON.stringify(filtersOf(a)) === JSON.stringify(filtersOf(b));
